import { useState } from "react";
import { Badge, Button, Card, Group, Skeleton, Stack, TextInput } from "@mantine/core";
import { IconSparkles } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import type { IngredientExtractOutput } from "@passio/api-client";
import { passio } from "../lib/clients";
import { PageHeader } from "../theme/components";
import { EmptyState, ErrorNotice, RequireFeature, ResultHeader } from "../components/common";
import { FoodDetailDrawer, FoodRow } from "../components/food";
import { FeedbackButtons } from "../components/FeedbackButtons";
import { RouteModelPicker } from "../components/ModelPicker";

export function AlternativesPage() {
  const [food, setFood] = useState("");
  const [context, setContext] = useState("");
  const [selected, setSelected] = useState<IngredientExtractOutput | null>(null);
  const alt = useMutation({ mutationFn: (v: { food: string; context?: string }) => passio.tools.visualAlternativeSuggestions(v.food, v.context) });
  const run = (f = food) => {
    if (!f.trim()) return;
    setFood(f);
    alt.mutate({ food: f, context: context.trim() || undefined });
  };

  return (
    <RequireFeature feature="text_ai">
      <PageHeader
        eyebrow="POST /napi/tools/visualAlternativeSuggestions"
        title="Look-alikes"
        description="Foods that look like the one a photo scan recognized, for when the image is ambiguous: the green slices in a salad might be cucumber or zucchini, a dark drink might be coffee, tea or cola. Show them as a “Did you mean…?” picker so users can correct a scan in one tap. Each option is matched to a real food with nutrition."
        actions={<RouteModelPicker routes={["visualAlternativeSuggestions"]} />}
      />
      <Card mb="lg">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            run();
          }}
        >
          <Stack gap="sm">
            <Group gap="sm" wrap="nowrap" align="flex-end">
              <TextInput style={{ flex: 2 }} size="md" label="Recognized food" placeholder="e.g. cucumber" value={food} onChange={(e) => setFood(e.currentTarget.value)} />
              <TextInput style={{ flex: 1 }} size="md" label="Context (optional)" placeholder="e.g. sliced, in a salad" value={context} onChange={(e) => setContext(e.currentTarget.value)} />
              <Button type="submit" size="md" leftSection={<IconSparkles size={16} />} loading={alt.isPending}>
                Suggest
              </Button>
            </Group>
            <Group gap={6}>
              {["cucumber slices", "black coffee", "mashed potatoes", "vanilla ice cream", "grilled chicken breast"].map((ex) => (
                <Badge key={ex} variant="outline" color="gray" tt="none" style={{ cursor: "pointer" }} onClick={() => run(ex)}>
                  {ex}
                </Badge>
              ))}
            </Group>
          </Stack>
        </form>
      </Card>
      {alt.error && <ErrorNotice error={alt.error} onRetry={() => run()} />}
      {alt.isPending && <Skeleton h={220} radius="lg" />}
      {alt.data && !alt.isPending && (
        <Card>
          <ResultHeader title={`Not ${food}? Did you mean…`} count={alt.data.data.length} meta={alt.data.meta} feedback={<FeedbackButtons requestId={alt.data.meta.requestId} itemRef={food} />} />
          <Stack gap={2}>
            {alt.data.data.map((it, i) => (
              <FoodRow key={i} item={it} onClick={it.refCode ? () => setSelected(it) : undefined} right={<FeedbackButtons requestId={alt.data.meta.requestId} itemRef={it.ingredientName ?? it.displayName} compact />} />
            ))}
          </Stack>
        </Card>
      )}
      {!alt.data && !alt.isPending && !alt.error && <EmptyState title="Enter what a scan recognized">The guided photo tour shows this in context: scan a photo, then offer look-alikes for the item the user wants to correct.</EmptyState>}
      <FoodDetailDrawer refCode={selected?.refCode} name={selected?.displayName} amount={selected} onClose={() => setSelected(null)} />
    </RequireFeature>
  );
}
